import { Apple, Check, Dumbbell, HeartPulse } from "lucide-react";

import { BookingScreen, DayScreen } from "@/features/portal/components/app-screens";
import { CoachFinder } from "@/features/portal/components/coach-finder";
import { Phone } from "@/features/portal/components/devices";
import { Accent, displayTitle, Eyebrow, GoLink, Monogram, Section } from "@/features/portal/components/portal-ui";
import { FloatChip, Stage } from "@/features/portal/components/stage";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

const FACTS = [
  () => m.coaches_page_fact_live(),
  () => m.coaches_page_fact_no_account(),
  () => m.coaches_page_fact_pay(),
];

/**
 * The words that open `/coaches`, inside the head card, beside the directory drawn as a night map
 * with a coach's page open on a phone. The map leads with the point of the page: people near you,
 * with times that are really free.
 */
export const CoachesHero = () => (
  <div className="grid items-center gap-10 pt-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14 lg:pt-14">
    <div className="px-1 max-lg:text-center">
      <Eyebrow>{m.portal_nav_coaches()}</Eyebrow>
      <h1 className={cn(displayTitle, "mt-6")}>
        {m.coaches_page_title_lead()} <Accent>{m.coaches_page_title_tail()}</Accent>
      </h1>
      <p className="mt-6 max-w-[46ch] text-lg leading-[1.55] text-pretty text-muted-foreground max-lg:mx-auto">
        {m.coaches_page_lede()}
      </p>
      <div className="mt-8 flex flex-wrap gap-3 max-lg:justify-center">
        <GoLink to="#coaches" tone="lime">
          {m.coaches_page_cta()}
        </GoLink>
      </div>
      <ul className="mt-10 grid gap-3 text-[15px] font-medium max-lg:mx-auto max-lg:w-fit max-lg:text-left">
        {FACTS.map((fact) => (
          <li key={fact()} className="flex items-center gap-3">
            <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
              <Check aria-hidden className="size-3.5" strokeWidth={3} />
            </span>
            {fact()}
          </li>
        ))}
      </ul>
    </div>
    <div className="dark relative isolate h-[440px] overflow-hidden rounded-[28px] bg-[oklch(0.13_0.004_120)] ring-1 ring-foreground/[0.07] ring-inset md:h-[560px]">
      <CoachFinder className="md:right-[34%]" />
      <span className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(60%_55%_at_70%_60%,color-mix(in_oklch,var(--primary)_22%,transparent),transparent)] max-md:hidden" />
      <Phone className="absolute top-10 right-[-4%] w-[36%] rotate-[4deg] max-md:hidden">
        <BookingScreen />
      </Phone>
      <FloatChip className="top-6 left-6">
        <i className="size-2 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
        {m.coaches_page_chip_free({ count: 12 })}
      </FloatChip>
    </div>
  </div>
);

/** Three sample people, each with the week's open times as the directory rows show them. */
const ROWS = [
  { id: "kasia", name: () => m.features_sample_coach_full(), role: () => m.coaches_page_role_trainer(), times: [[], ["17:30", "18:30"], ["07:00"], [], ["16:00"]] },
  { id: "michal", name: () => m.coaches_page_sample_two(), role: () => m.coaches_page_role_physio(), times: [["08:00"], [], ["12:30", "13:30"], ["09:00"], []] },
  { id: "ania", name: () => m.coaches_page_sample_three(), role: () => m.coaches_page_role_dietician(), times: [["10:00"], ["11:00"], [], ["15:00", "16:00"], ["10:00"]] },
];

const DAY_INDEXES = [0, 1, 2, 3, 4];

/** A Monday that is always a Monday, so the week has the same day names on the server and in the browser. */
const MONDAY = Date.UTC(2026, 10, 2);
const dayName = (index: number) =>
  new Intl.DateTimeFormat(getLocale(), { weekday: "short", timeZone: "UTC" }).format(
    MONDAY + index * 86_400_000,
  );

/** The open calendar: a row per person, a column per day, the free times as chips to tap. */
const OpenWeek = () => (
  <div aria-hidden className="grid gap-2 rounded-[26px] bg-foreground/[0.04] p-3 ring-1 ring-foreground/[0.06] ring-inset">
    <div className="grid grid-cols-[minmax(0,1.3fr)_repeat(5,minmax(0,1fr))] gap-2 px-2 pt-1 text-[12px] text-muted-foreground">
      <span />
      {DAY_INDEXES.map((index) => (
        <span key={index} className={cn("text-center capitalize", index === 1 && "font-semibold text-foreground")}>
          {dayName(index)}
        </span>
      ))}
    </div>
    {ROWS.map((row) => (
      <div
        key={row.id}
        className="grid grid-cols-[minmax(0,1.3fr)_repeat(5,minmax(0,1fr))] items-center gap-2 rounded-[18px] bg-foreground/[0.04] p-2"
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <Monogram name={row.name()} className="size-9 rounded-[12px] text-[12px]" />
          <span className="min-w-0 leading-tight">
            <b className="block truncate text-[13px] font-semibold">{row.name()}</b>
            <small className="block truncate text-[12px] text-muted-foreground">{row.role()}</small>
          </span>
        </span>
        {row.times.map((times, day) => (
          <span key={DAY_INDEXES[day]} className="grid gap-1">
            {times.length ? (
              times.map((time) => (
                <span
                  key={time}
                  className={cn(
                    "grid h-7 place-items-center rounded-full text-[12px] font-semibold tabular-nums",
                    row.id === "kasia" && time === "17:30"
                      ? "bg-primary text-primary-foreground shadow-[0_6px_18px_-6px_var(--primary)]"
                      : "bg-foreground/[0.08]",
                  )}
                >
                  {time}
                </span>
              ))
            ) : (
              <span className="mx-auto h-0.5 w-3 rounded-full bg-foreground/15" />
            )}
          </span>
        ))}
      </div>
    ))}
  </div>
);

const PROFESSIONS = [
  { id: "trainer", icon: Dumbbell, name: () => m.coaches_page_role_trainer(), spot: "-translate-x-[92%] translate-y-[8%] -rotate-[9deg]" },
  { id: "physio", icon: HeartPulse, name: () => m.coaches_page_role_physio(), spot: "z-10 -translate-x-1/2 -translate-y-[6%]" },
  { id: "dietician", icon: Apple, name: () => m.coaches_page_role_dietician(), spot: "-translate-x-[8%] translate-y-[8%] rotate-[9deg] items-end text-right" },
];

/** The three kinds of specialist, spread like a hand of cards, the middle one lit. */
const Professions = () => (
  <div aria-hidden className="absolute inset-0">
    {PROFESSIONS.map((profession) => (
      <span
        key={profession.id}
        className={cn(
          "absolute top-[16%] left-1/2 flex h-[210px] w-[min(170px,40%)] origin-bottom flex-col justify-between rounded-[26px] bg-[linear-gradient(150deg,oklch(0.27_0.01_120),oklch(0.18_0.006_120))] p-5 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.85)] ring-1 ring-white/10 ring-inset",
          profession.spot,
          profession.id === "physio" &&
            "bg-[linear-gradient(150deg,color-mix(in_oklch,var(--primary)_92%,white),var(--primary)_50%,color-mix(in_oklch,var(--primary)_70%,black))] text-primary-foreground ring-0",
        )}
      >
        <span
          className={cn(
            "grid size-11 place-items-center rounded-[14px]",
            profession.id === "physio" ? "bg-primary-foreground/12" : "bg-white/10",
          )}
        >
          <profession.icon className="size-5" />
        </span>
        <b className="text-[19px] leading-tight font-semibold tracking-[-0.02em] break-words">{profession.name()}</b>
      </span>
    ))}
  </div>
);

/** Why book here, after the list: the open week, who is on it, and what waits after the visit. */
export const CoachesShowcase = () => (
  <Section>
    <div className="mb-10 max-w-[780px] px-1">
      <Eyebrow>{m.coaches_page_why_eyebrow()}</Eyebrow>
      <h2 className={cn(displayTitle, "mt-5")}>
        {m.coaches_page_why_lead()} <Accent>{m.coaches_page_why_tail()}</Accent>
      </h2>
    </div>
    <div className="grid gap-3 md:grid-cols-2">
      <Stage
        side
        title={m.coaches_page_week_title()}
        lead={m.coaches_page_week_lead()}
        body={m.coaches_page_week_body()}
        className="md:col-span-2"
        artClassName="flex items-center px-5 pb-8 lg:py-14 lg:pr-12 lg:pl-0"
      >
        <div className="w-full">
          <OpenWeek />
        </div>
      </Stage>
      <Stage
        lead={m.coaches_page_kinds_lead()}
        body={m.coaches_page_kinds_body()}
        className="h-[560px] md:h-[600px]"
      >
        <Professions />
      </Stage>
      <Stage
        lead={m.coaches_page_after_lead()}
        body={m.coaches_page_after_body()}
        className="h-[620px] md:h-[600px]"
        fade
      >
        <Phone className="absolute top-0 left-1/2 w-[min(300px,82%)] -translate-x-1/2">
          <DayScreen />
        </Phone>
      </Stage>
    </div>
  </Section>
);
